import { StyledPropsSelector } from './utils/typeUtils';
import clsx from 'clsx';
import React, { MouseEventHandler, forwardRef, HTMLAttributes } from 'react';
import styled from 'styled-components';

interface Props extends HTMLAttributes<HTMLButtonElement> {
	onClick?: MouseEventHandler<HTMLButtonElement>;
	variant?: 'filled' | 'outlined' | 'text';
	color?: 'primary';
}

const buttonBorder: StyledPropsSelector<Props> = (state?) => (props) => {
	const { variant = 'filled', theme } = props;

	if (state === 'hover') {
		return variant === 'text'
			? 'none'
			: `2px solid ${theme.palette.primary.main}`;
	}
	return 'none'
};


const buttonBackground: StyledPropsSelector<Props> = (state?) => (props) => {
	const { variant = 'filled', theme } = props;

	if (state === 'hover') {
		return variant === 'filled' || variant === 'text'
			? 'transparent'
			: theme.palette.primary.main + '30';
	}
	return variant === 'filled' ? theme.palette.primary.main :
		variant === 'text' ? 'transparent' : theme.palette.primary.main;
};

const buttonTextColor: StyledPropsSelector<Props> = (state?) => (props) => {
	const { variant = 'filled', theme } = props;

	if (state === 'hover') {
		return variant === 'filled'
			? theme.palette.primary.main
			: theme.palette.common.white;
	}

	return variant === 'filled'
		? theme.palette.common.white
		: theme.palette.primary.main;
};

const StyledButtonBase = styled.button`
	color: ${buttonTextColor()};
	background-color: ${buttonBackground()};
	padding: ${(p) => p.theme.spacing(0.5, 1)};
	border-radius: ${(p) => p.theme.spacing(1)};
	border: ${buttonBorder()};
	font-weight: ${(p) => p.theme.typography.button.fontWeight};
	text-transform: ${(p) => p.theme.typography.button.textTransform};
	letter-spacing: ${(p) => p.theme.typography.button.letterSpacing};
	font-family: ${(p) => p.theme.typography.button.fontFamily};
	font-size: ${(p) => p.theme.typography.button.fontSize};
	line-height: ${(p) => p.theme.typography.button.lineHeight};
	transition: background 0.075s linear;

	&:hover {
		color: ${buttonTextColor('hover')};
		background-color: ${buttonBackground('hover')};
		border: ${buttonBorder('hover')};
		cursor: pointer;
	}

	&.button-disabled {
		color: ${(p) => p.theme.palette.divider};
		border: 2px solid ${(p) => p.theme.palette.divider};
		background-color: transparent;
		cursor: revert;
	}
`;

const Button = forwardRef<Props, any>(
	({ disabled, className, ...props }, ref) => {
		return (
			<StyledButtonBase
				className={clsx(className, disabled && 'button-disabled')}
				{...props}
				disabled={disabled}
				ref={ref}
			/>
		);
	}
);

export default Button;
